import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/base/Loading";
import { formatFullDate, formatRelativeTime } from "@/lib/format";
import { downloadCsv, type CsvColumn } from "@/lib/csv";
import { useLicenseDevices, type LicenseDevice } from "@/pages/licenses/hooks/useLicenseDevices";
import type { License } from "@/pages/licenses/hooks/useMyLicenses";

type DeviceStatusFilter = "all" | "active" | "released";
type DeviceTimeFilter = "all" | "7d" | "30d";

const PAGE_INACTIVE_DAYS = 30;

type ReleaseSource = "self" | "batch";

interface TimelineEvent {
  id: string;
  type: "activated" | "released";
  source: ReleaseSource;
  device: LicenseDevice;
  time: string;
}

interface BatchGroup {
  kind: "batch";
  id: string;
  time: string;
  devices: LicenseDevice[];
}

interface SingleEntry {
  kind: "single";
  id: string;
  time: string;
  event: TimelineEvent;
}

type TimelineEntry = BatchGroup | SingleEntry;

interface DevicePanelProps {
  license: License;
  onRefresh?: () => void;
  onClose: () => void;
}

export default function DevicePanel({ license, onRefresh, onClose }: DevicePanelProps) {
  const { t } = useTranslation();
  const { devices, loading, error, reload, release, rename, activate, releaseInactive } = useLicenseDevices(license.id);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [showReleased, setShowReleased] = useState(false);
  const [feedback, setFeedback] = useState<{ text: string; tone: "success" | "error" } | null>(null);
  const [activateOpen, setActivateOpen] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [expandedBatch, setExpandedBatch] = useState<string | null>(null);
  const [exported, setExported] = useState(false);
  const [statusFilter, setStatusFilter] = useState<DeviceStatusFilter>("all");
  const [timeFilter, setTimeFilter] = useState<DeviceTimeFilter>("all");
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const [inactiveConfirm, setInactiveConfirm] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [machineCode, setMachineCode] = useState("");
  const [deviceName, setDeviceName] = useState("");
  const [os, setOs] = useState("");
  const [locationInput, setLocationInput] = useState("");
  const [activating, setActivating] = useState(false);

  const STATUS_FILTERS: { key: DeviceStatusFilter; label: string }[] = [
    { key: "all", label: t("device.statusAll") },
    { key: "active", label: t("device.statusActive") },
    { key: "released", label: t("device.statusReleased") },
  ];

  const TIME_FILTERS: { key: DeviceTimeFilter; label: string }[] = [
    { key: "all", label: t("device.timeAll") },
    { key: "7d", label: t("device.time7d") },
    { key: "30d", label: t("device.time30d") },
  ];

  const deviceStatusLabel = (status: string) => (status === "active" ? t("device.statusActive") : t("device.statusReleased"));

  const DEVICE_CSV_COLUMNS: CsvColumn<LicenseDevice>[] = [
    { key: "device_name", label: t("device.colDevice"), value: (row) => row.device_name },
    { key: "machine_code", label: t("device.colMachine"), value: (row) => row.machine_code ?? "" },
    { key: "os", label: t("device.colOs"), value: (row) => row.os ?? "" },
    { key: "location", label: t("device.colLocation"), value: (row) => row.location ?? "" },
    { key: "status", label: t("device.colStatus"), value: (row) => deviceStatusLabel(row.status) },
    { key: "activated_at", label: t("device.colActivated"), value: (row) => (row.activated_at ? formatFullDate(row.activated_at) : "") },
    { key: "last_seen_at", label: t("device.colLastSeen"), value: (row) => (row.last_seen_at ? formatFullDate(row.last_seen_at) : "") },
    { key: "released_at", label: t("device.colReleased"), value: (row) => (row.released_at ? formatFullDate(row.released_at) : "") },
    {
      key: "release_source",
      label: t("device.colReleaseSource"),
      value: (row) =>
        row.status === "active" ? "" : row.release_source === "batch" ? t("device.sourceBatch") : t("device.sourceSelf"),
    },
  ];

  const allActive = devices.filter((item) => item.status === "active");
  const seatsLeft = Math.max(0, (license.max_activations ?? 0) - allActive.length);

  const inactiveDevices = allActive.filter((item) => {
    const seen = item.last_seen_at ?? item.activated_at;
    if (!seen) return true;
    const time = new Date(seen).getTime();
    return Number.isNaN(time) ? true : Date.now() - time > PAGE_INACTIVE_DAYS * 86400000;
  });

  const filteredDevices = devices.filter((item) => {
    if (statusFilter === "active" && item.status !== "active") return false;
    if (statusFilter === "released" && item.status === "active") return false;
    if (timeFilter !== "all") {
      if (!item.activated_at) return false;
      const days = timeFilter === "7d" ? 7 : 30;
      if (Date.now() - new Date(item.activated_at).getTime() > days * 86400000) return false;
    }
    return true;
  });
  const activeDevices = filteredDevices.filter((item) => item.status === "active");
  const releasedDevices = filteredDevices.filter((item) => item.status !== "active");

  const timeline = useMemo<TimelineEvent[]>(() => {
    const events: TimelineEvent[] = [];
    devices.forEach((device) => {
      if (device.activated_at) {
        events.push({ id: `a-${device.id}`, type: "activated", source: "self", device, time: device.activated_at });
      }
      if (device.status !== "active" && device.released_at) {
        events.push({
          id: `r-${device.id}`,
          type: "released",
          source: device.release_source === "batch" ? "batch" : "self",
          device,
          time: device.released_at,
        });
      }
    });
    return events.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
  }, [devices]);

  const timelineEntries = useMemo<TimelineEntry[]>(() => {
    const batchMap = new Map<string, BatchGroup>();
    const singles: SingleEntry[] = [];
    timeline.forEach((event) => {
      if (event.type === "released" && event.source === "batch") {
        const key = event.time;
        const group = batchMap.get(key) ?? { kind: "batch", id: `b-${key}`, time: event.time, devices: [] };
        group.devices.push(event.device);
        batchMap.set(key, group);
      } else {
        singles.push({ kind: "single", id: event.id, time: event.time, event });
      }
    });
    return [...Array.from(batchMap.values()), ...singles].sort(
      (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime(),
    );
  }, [timeline]);

  const handleExport = () => {
    if (devices.length === 0) return;
    const stamp = new Date().toISOString().slice(0, 10);
    const safeName = license.product_name.replace(/[\\/:*?"<>|\s]+/g, "-");
    downloadCsv(`${t("device.csvFileName")}_${safeName}_${stamp}.csv`, DEVICE_CSV_COLUMNS, devices);
    setExported(true);
    window.setTimeout(() => setExported(false), 2000);
  };

  const jumpToDevice = (device: LicenseDevice) => {
    setStatusFilter("all");
    setTimeFilter("all");
    if (device.status !== "active") setShowReleased(true);
    setHighlightId(device.id);
    window.setTimeout(() => {
      document.getElementById(`device-row-${device.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
    window.setTimeout(() => setHighlightId(null), 2600);
  };

  const resetActivateForm = () => {
    setMachineCode("");
    setDeviceName("");
    setOs("");
    setLocationInput("");
  };

  const handleRelease = async (device: LicenseDevice) => {
    setBusyId(device.id);
    try {
      await release(device.id);
      setFeedback({ text: t("device.releaseSuccess", { name: device.device_name }), tone: "success" });
      onRefresh?.();
    } catch (err) {
      setFeedback({ text: err instanceof Error ? err.message : t("device.releaseFailed"), tone: "error" });
    } finally {
      setBusyId(null);
      setConfirmId(null);
    }
  };

  const handleRename = async (device: LicenseDevice) => {
    const name = editName.trim();
    if (!name) return;
    setBusyId(device.id);
    try {
      await rename(device.id, name);
      setFeedback({ text: t("device.renameSuccess"), tone: "success" });
    } catch (err) {
      setFeedback({ text: err instanceof Error ? err.message : t("device.renameFailed"), tone: "error" });
    } finally {
      setBusyId(null);
      setEditingId(null);
    }
  };

  const handleActivate = async () => {
    const code = machineCode.trim();
    if (!code) {
      setFeedback({ text: t("device.needMachineCode"), tone: "error" });
      return;
    }
    setActivating(true);
    try {
      await activate({
        machineCode: code,
        deviceName: deviceName.trim() || undefined,
        os: os.trim() || undefined,
        location: locationInput.trim() || undefined,
      });
      const left = Math.max(0, seatsLeft - 1);
      setFeedback({ text: t("device.activateSuccess", { left }), tone: "success" });
      resetActivateForm();
      setActivateOpen(false);
      onRefresh?.();
    } catch (err) {
      setFeedback({ text: err instanceof Error ? err.message : t("device.activateFailed"), tone: "error" });
    } finally {
      setActivating(false);
    }
  };

  const startRename = (device: LicenseDevice) => {
    setEditingId(device.id);
    setEditName(device.device_name);
    setConfirmId(null);
  };

  const handleCleanInactive = async () => {
    setCleaning(true);
    try {
      const result = await releaseInactive(PAGE_INACTIVE_DAYS);
      setFeedback({
        text:
          result.released > 0
            ? t("device.cleanSuccess", { count: result.released })
            : t("device.cleanNone"),
        tone: "success",
      });
      onRefresh?.();
    } catch (err) {
      setFeedback({ text: err instanceof Error ? err.message : t("device.cleanFailed"), tone: "error" });
    } finally {
      setCleaning(false);
      setInactiveConfirm(false);
    }
  };

  return (
    <div className="mt-3 rounded-md border border-primary-200 bg-primary-50/70 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
            <span className="flex h-4 w-4 items-center justify-center text-primary-700">
              <i className="ri-device-line" />
            </span>
            {t("device.panelTitle", { product: license.product_name })}
          </p>
          <p className="mt-1 text-[11px] text-foreground-500">
            {t("device.summary", {
              active: activeDevices.length,
              max: license.max_activations,
              left: seatsLeft,
            })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={seatsLeft <= 0}
            onClick={() => {
              setActivateOpen((value) => !value);
              setFeedback(null);
            }}
            title={seatsLeft <= 0 ? t("device.activateSeatsUsed") : t("device.activateTitle")}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className={activateOpen ? "ri-close-line" : "ri-add-line"} />
            </span>
            {t("device.activateNew")}
          </button>
          <button
            type="button"
            disabled={devices.length === 0}
            onClick={handleExport}
            title={devices.length === 0 ? t("device.exportEmpty") : t("device.exportTitle")}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-700">
              <i className={exported ? "ri-check-line" : "ri-download-2-line"} />
            </span>
            {exported ? t("device.exported") : t("device.exportList")}
          </button>
          <button
            type="button"
            disabled={inactiveDevices.length === 0 || cleaning}
            onClick={() => {
              setInactiveConfirm((value) => !value);
              setFeedback(null);
            }}
            title={
              inactiveDevices.length === 0
                ? t("device.cleanNoInactive", { days: PAGE_INACTIVE_DAYS })
                : t("device.cleanTitle", { count: inactiveDevices.length, days: PAGE_INACTIVE_DAYS })
            }
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:border-accent-300 hover:bg-accent-50 hover:text-accent-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center text-accent-700">
              <i className="ri-delete-bin-6-line" />
            </span>
            {t("device.cleanInactive")}
            {inactiveDevices.length > 0 ? `（${inactiveDevices.length}）` : ""}
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("device.close")}
            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      </div>

      {inactiveConfirm && inactiveDevices.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5">
          <p className="flex items-start gap-2 text-xs text-accent-800">
            <span className="mt-0.5 flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-information-line" />
            </span>
            <span className="flex-1">{t("device.cleanConfirm", { count: inactiveDevices.length, days: PAGE_INACTIVE_DAYS })}</span>
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={cleaning}
              onClick={() => setInactiveConfirm(false)}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-background-100"
            >
              {t("device.cancel")}
            </button>
            <button
              type="button"
              disabled={cleaning}
              onClick={() => {
                void handleCleanInactive();
              }}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-600 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-accent-700 disabled:opacity-60"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-delete-bin-6-line" />
              </span>
              {cleaning ? t("device.cleaning") : t("device.confirmUnbind", { count: inactiveDevices.length })}
            </button>
          </div>
        </div>
      )}

      {activateOpen && seatsLeft > 0 && (
        <div className="mt-3 rounded-md border border-primary-200 bg-background-50 p-4">
          <p className="flex items-center gap-2 text-xs font-medium text-foreground-800">
            <span className="flex h-4 w-4 items-center justify-center text-primary-700">
              <i className="ri-keyboard-line" />
            </span>
            {t("device.activateForm", { left: seatsLeft })}
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-[11px] text-foreground-500">
                {t("device.machineCode")} <span className="text-accent-700">*</span>
              </span>
              <input
                type="text"
                value={machineCode}
                maxLength={64}
                onChange={(event) => setMachineCode(event.target.value)}
                placeholder={t("device.machineCodePlaceholder")}
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 font-mono text-xs text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-foreground-500">{t("device.deviceName")}</span>
              <input
                type="text"
                value={deviceName}
                maxLength={40}
                onChange={(event) => setDeviceName(event.target.value)}
                placeholder={t("device.deviceNamePlaceholder")}
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-foreground-500">{t("device.os")}</span>
              <input
                type="text"
                value={os}
                maxLength={40}
                onChange={(event) => setOs(event.target.value)}
                placeholder={t("device.osPlaceholder")}
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-foreground-500">{t("device.location")}</span>
              <input
                type="text"
                value={locationInput}
                maxLength={60}
                onChange={(event) => setLocationInput(event.target.value)}
                placeholder={t("device.locationPlaceholder")}
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </label>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              disabled={activating}
              onClick={() => {
                setActivateOpen(false);
                resetActivateForm();
              }}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-background-100"
            >
              {t("device.cancel")}
            </button>
            <button
              type="button"
              disabled={activating || !machineCode.trim()}
              onClick={() => {
                void handleActivate();
              }}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-add-circle-line" />
              </span>
              {activating ? t("device.activating") : t("device.confirmActivate")}
            </button>
          </div>
        </div>
      )}

      {feedback && (
        <div
          className={`mt-3 flex items-start gap-2 rounded-md px-3 py-2 text-xs ${
            feedback.tone === "error" ? "bg-accent-50 text-accent-800" : "bg-primary-100 text-primary-700"
          }`}
        >
          <span className="mt-0.5 flex h-3.5 w-3.5 items-center justify-center">
            <i className={feedback.tone === "error" ? "ri-error-warning-line" : "ri-checkbox-circle-line"} />
          </span>
          <span className="flex-1">{feedback.text}</span>
        </div>
      )}

      {!loading && !error && devices.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-primary-200 pt-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded-full border border-background-300 bg-background-50 p-1">
              {STATUS_FILTERS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setStatusFilter(item.key)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-[11px] transition-colors ${
                    statusFilter === item.key
                      ? "bg-background-100 font-medium text-foreground-950"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="inline-flex rounded-full border border-background-300 bg-background-50 p-1">
              {TIME_FILTERS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTimeFilter(item.key)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-[11px] transition-colors ${
                    timeFilter === item.key
                      ? "bg-background-100 font-medium text-foreground-950"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <span className="text-[11px] text-foreground-500">
            {t("device.summaryCount", { total: devices.length, filtered: filteredDevices.length })}
          </span>
        </div>
      )}

      {loading ? (
        <div className="mt-3 space-y-2">
          {[0, 1, 2].map((item) => (
            <div key={item} className="flex items-center gap-3 rounded-md border border-background-200 bg-background-50 p-3">
              <Skeleton className="h-8 w-8 rounded-md" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3.5 w-1/3" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="mt-3 flex flex-col items-center gap-2 rounded-md border border-background-200 bg-background-50 py-8 text-center">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-700">
            <i className="ri-error-warning-line text-lg" />
          </span>
          <p className="text-xs text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="cursor-pointer rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            {t("device.reload")}
          </button>
        </div>
      ) : activeDevices.length === 0 ? (
        <div className="mt-3 flex flex-col items-center gap-2 rounded-md border border-background-200 bg-background-50 py-8 text-center">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className={`${devices.length === 0 ? "ri-device-line" : "ri-filter-off-line"} text-lg`} />
          </span>
          <p className="text-xs text-foreground-600">
            {devices.length === 0 ? t("device.emptyNoDevice") : t("device.emptyNoMatch")}
          </p>
          <p className="max-w-xs text-[11px] text-foreground-400">
            {devices.length === 0 ? t("device.emptyNoDeviceDesc") : t("device.emptyNoMatchDesc")}
          </p>
        </div>
      ) : (
        <ul className="mt-3 flex flex-col gap-2">
          {activeDevices.map((device) => {
            const confirming = confirmId === device.id;
            const editing = editingId === device.id;
            const busy = busyId === device.id;
            return (
              <li
                key={device.id}
                id={`device-row-${device.id}`}
                className={`rounded-md border bg-background-50 p-3 transition-colors ${
                  highlightId === device.id ? "border-accent-400 ring-1 ring-accent-300" : "border-background-200"
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-computer-line text-base" />
                    </span>
                    <div className="min-w-0">
                      {editing ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            type="text"
                            value={editName}
                            maxLength={40}
                            onChange={(event) => setEditName(event.target.value)}
                            className="w-44 rounded-md border border-background-300 bg-background-50 px-2 py-1 text-xs text-foreground-900 focus:border-primary-400 focus:outline-none"
                          />
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              void handleRename(device);
                            }}
                            className="cursor-pointer rounded-md bg-primary-500 px-2.5 py-1 text-[11px] font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
                          >
                            {t("device.save")}
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingId(null)}
                            className="cursor-pointer rounded-md border border-background-300 px-2.5 py-1 text-[11px] text-foreground-700 transition-colors hover:bg-background-100"
                          >
                            {t("device.cancel")}
                          </button>
                        </div>
                      ) : (
                        <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
                          {device.device_name}
                          <button
                            type="button"
                            onClick={() => startRename(device)}
                            aria-label={t("device.rename")}
                            className="flex h-5 w-5 cursor-pointer items-center justify-center rounded text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-700"
                          >
                            <i className="ri-edit-line text-xs" />
                          </button>
                        </p>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground-500">
                        {device.machine_code && <span className="font-mono">{device.machine_code}</span>}
                        {device.os && <span>{device.os}</span>}
                        {device.location && (
                          <span className="flex items-center gap-1">
                            <span className="flex h-3 w-3 items-center justify-center">
                              <i className="ri-map-pin-line" />
                            </span>
                            {device.location}
                          </span>
                        )}
                        {device.last_seen_at && (
                          <span>{t("device.lastSeen", { time: formatRelativeTime(device.last_seen_at) })}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {confirming ? (
                      <>
                        <span className="text-[11px] text-accent-800">{t("device.confirmRelease")}</span>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            void handleRelease(device);
                          }}
                          className="cursor-pointer whitespace-nowrap rounded-md bg-accent-600 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-accent-700 disabled:opacity-60"
                        >
                          {busy ? t("device.processing") : t("device.confirmReleaseBtn")}
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => setConfirmId(null)}
                          className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-background-100"
                        >
                          {t("device.cancel")}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmId(device.id)}
                        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:border-accent-300 hover:bg-accent-50 hover:text-accent-800"
                      >
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i className="ri-link-unlink" />
                        </span>
                        {t("device.releaseBtn")}
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {timeline.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowTimeline((value) => !value)}
            className="flex cursor-pointer items-center gap-1.5 text-[11px] font-medium text-foreground-600 transition-colors hover:text-foreground-900"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className={showTimeline ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"} />
            </span>
            {t("device.timelineTitle", { count: timelineEntries.length })}
          </button>
          {showTimeline && (
            <ol className="mt-3 flex flex-col">
              {timelineEntries.map((entry, index) => {
                const isLast = index === timelineEntries.length - 1;
                if (entry.kind === "batch") {
                  const expanded = expandedBatch === entry.id;
                  return (
                    <li key={entry.id} className="relative flex gap-3 pb-3 last:pb-0">
                      <div className="flex flex-col items-center">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                          <i className="ri-delete-bin-6-line text-xs" />
                        </span>
                        {!isLast && <span className="mt-1 w-px flex-1 bg-background-200" />}
                      </div>
                      <div className="min-w-0 flex-1 pb-1">
                        <button
                          type="button"
                          onClick={() => setExpandedBatch(expanded ? null : entry.id)}
                          title={t("device.expandBatch")}
                          className="flex w-full cursor-pointer items-center gap-2 rounded-md py-0.5 text-left transition-colors hover:bg-background-50"
                        >
                          <p className="flex flex-wrap items-center gap-1.5 text-xs font-medium text-foreground-900">
                            {t("device.batchClean", { count: entry.devices.length })}
                            <span className="rounded-full bg-accent-100 px-1.5 py-0.5 text-[9px] font-medium text-accent-800">
                              {t("device.batchTag")}
                            </span>
                          </p>
                          <span className="ml-auto flex h-4 w-4 items-center justify-center text-foreground-400">
                            <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                          </span>
                        </button>
                        <p className="mt-0.5 text-[10px] text-foreground-500">{formatFullDate(entry.time)}</p>
                        {expanded && (
                          <ul className="mt-2 flex flex-col gap-1.5">
                            {entry.devices.map((device) => (
                              <li key={device.id}>
                                <button
                                  type="button"
                                  onClick={() => jumpToDevice(device)}
                                  title={t("device.jumpDevice")}
                                  className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-md border border-background-200 bg-background-50 px-2.5 py-1.5 text-left transition-colors hover:border-accent-300 hover:bg-accent-50"
                                >
                                  <span className="min-w-0 truncate text-[11px] text-foreground-800">
                                    {device.device_name}
                                  </span>
                                  <span className="flex shrink-0 items-center gap-1.5 text-[10px] text-foreground-400">
                                    {device.machine_code && <span className="font-mono">{device.machine_code}</span>}
                                    <i className="ri-arrow-right-up-line" />
                                  </span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </li>
                  );
                }
                const event = entry.event;
                const released = event.type === "released";
                return (
                  <li key={entry.id} className="relative flex gap-3 pb-3 last:pb-0">
                    <div className="flex flex-col items-center">
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                          released ? "bg-background-200 text-foreground-600" : "bg-primary-100 text-primary-700"
                        }`}
                      >
                        <i className={`${released ? "ri-link-unlink" : "ri-add-line"} text-xs`} />
                      </span>
                      {!isLast && <span className="mt-1 w-px flex-1 bg-background-200" />}
                    </div>
                    <button
                      type="button"
                      onClick={() => jumpToDevice(event.device)}
                      title={t("device.jumpDevice")}
                      className="min-w-0 flex-1 cursor-pointer rounded-md pb-1 text-left transition-colors hover:bg-background-50"
                    >
                      <p className="flex flex-wrap items-center gap-1.5 text-xs font-medium text-foreground-900">
                        {released ? t("device.eventRelease") : t("device.eventActivate")} · {event.device.device_name}
                        <span className="rounded-full bg-secondary-100 px-1.5 py-0.5 text-[9px] font-medium text-secondary-900">
                          {released ? t("device.tagSelfRelease") : t("device.tagSelfActivate")}
                        </span>
                        <span className="flex h-3.5 w-3.5 items-center justify-center text-foreground-400">
                          <i className="ri-arrow-right-up-line" />
                        </span>
                      </p>
                      <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-foreground-500">
                        {event.device.machine_code && <span className="font-mono">{event.device.machine_code}</span>}
                        <span>{formatFullDate(event.time)}</span>
                      </p>
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}

      {releasedDevices.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowReleased((value) => !value)}
            className="flex cursor-pointer items-center gap-1.5 text-[11px] font-medium text-foreground-600 transition-colors hover:text-foreground-900"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className={showReleased ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"} />
            </span>
            {t("device.releasedDevices", { count: releasedDevices.length })}
          </button>
          {showReleased && (
            <ul className="mt-2 flex flex-col gap-2">
              {releasedDevices.map((device) => (
                <li
                  key={device.id}
                  id={`device-row-${device.id}`}
                  className={`flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2.5 transition-colors ${
                    highlightId === device.id
                      ? "border-accent-400 bg-accent-50 ring-1 ring-accent-300"
                      : "border-background-200 bg-background-100"
                  }`}
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background-200 text-foreground-500">
                      <i className="ri-computer-line text-sm" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-xs text-foreground-700">{device.device_name}</p>
                      <p className="font-mono text-[10px] text-foreground-400">{device.machine_code}</p>
                    </div>
                  </div>
                  <span className="flex items-center gap-2 text-[10px] text-foreground-500">
                    <span className="rounded-full bg-background-200 px-2 py-0.5 text-foreground-600">
                      {t("device.statusReleased")}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 ${
                        device.release_source === "batch"
                          ? "bg-accent-100 text-accent-800"
                          : "bg-secondary-100 text-secondary-900"
                      }`}
                    >
                      {device.release_source === "batch" ? t("device.sourceBatch") : t("device.sourceSelf")}
                    </span>
                    {device.released_at && <span>{formatFullDate(device.released_at)}</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}